import type { BestTimeWeek, WeightPreset } from "@/lib/conditions/best-time-data";
import { scoreOutOf100 } from "@/lib/conditions/score-display";

export function getEffectiveWeights(
  preset: WeightPreset,
  hasPricing: boolean
): { wC: number; wQ: number; wV: number } {
  if (hasPricing) {
    return { wC: preset.conditions, wQ: preset.quiet, wV: preset.value };
  }
  const total = preset.conditions + preset.quiet;
  return {
    wC: preset.conditions / total,
    wQ: preset.quiet / total,
    wV: 0,
  };
}

export function calcOverallScore(
  week: BestTimeWeek,
  weights: { wC: number; wQ: number; wV: number }
): number {
  const conditionsComponent = week.conditionsNorm * weights.wC;
  const quietComponent = (1 - week.crowdPressure) * weights.wQ;
  const valueComponent =
    weights.wV > 0 && week.priceNorm !== undefined
      ? (1 - week.priceNorm) * weights.wV
      : 0;
  return conditionsComponent + quietComponent + valueComponent;
}

/**
 * Index of the highest scoring week in the window. Weeks without a historical
 * conditions score are never picked while any week in the window has one, so
 * a recommendation always rests on a real score.
 */
export function findBestWeekIndex(
  weeks: BestTimeWeek[],
  weights: { wC: number; wQ: number; wV: number },
  windowStart?: number,
  windowEnd?: number
): number {
  const start = windowStart ?? 0;
  const end = windowEnd ?? weeks.length - 1;

  const pick = (skipMissing: boolean): number | null => {
    let bestIdx: number | null = null;
    let bestScore = -1;
    for (let i = start; i <= end && i < weeks.length; i++) {
      if (skipMissing && weeks[i].conditionsMissing) continue;
      const score = calcOverallScore(weeks[i], weights);
      if (score > bestScore) {
        bestScore = score;
        bestIdx = i;
      }
    }
    return bestIdx;
  };

  return pick(true) ?? pick(false) ?? 0;
}

/** Whether any week of the entry has a real historical conditions score. */
export function hasConditionsScore(entry: { weeks: readonly BestTimeWeek[] }): boolean {
  return entry.weeks.some((week) => !week.conditionsMissing);
}

/** Heatmap colours for a week with no historical conditions score. */
export const NO_SCORE_BAND: ConditionsBand = {
  colour: "#e5e7eb",
  fg: "#7A7A7A",
  label: "No score yet",
};

/** Colour band for a week, or the neutral no-score band when it has none. */
export function weekConditionsBand(week: BestTimeWeek): ConditionsBand {
  return week.conditionsMissing ? NO_SCORE_BAND : conditionsBand(week.conditions);
}

/** Accessible label for one heatmap week, e.g. "Mar W3: 72/100 Great". */
export function weekConditionsTitle(week: BestTimeWeek): string {
  const label = formatWeekLabel(week.weekStart);
  return week.conditionsMissing
    ? `${label}: no historical score yet`
    : `${label}: ${scoreOutOf100(week.conditions)}/100 ${conditionsBand(week.conditions).label}`;
}

/** Short cell text for one heatmap week: the 0 to 100 score, or "n/a". */
export function weekConditionsCellText(week: BestTimeWeek): string {
  return week.conditionsMissing ? "n/a" : String(Math.round(week.conditions * 10));
}

export function getBestOverallScore(
  weeks: BestTimeWeek[],
  weights: { wC: number; wQ: number; wV: number },
  windowStart?: number,
  windowEnd?: number
): number {
  const idx = findBestWeekIndex(weeks, weights, windowStart, windowEnd);
  return calcOverallScore(weeks[idx], weights);
}

export type ConditionsBand = {
  colour: string;
  fg: string;
  label: string;
};

export function conditionsBand(score: number): ConditionsBand {
  if (score >= 8) return { colour: "#1a3a5c", fg: "#ffffff", label: "Epic" };
  if (score >= 6.5) return { colour: "#1e4d7a", fg: "#ffffff", label: "Great" };
  if (score >= 5) return { colour: "#2a5f8f", fg: "#ffffff", label: "Good" };
  if (score >= 3.5) return { colour: "#7a8fa0", fg: "#ffffff", label: "Fair" };
  return { colour: "#c4ccd4", fg: "#1B2840", label: "Poor" };
}

export const CONDITIONS_LEGEND: { min: number; colour: string; label: string }[] = [
  { min: 8, colour: "#1a3a5c", label: "Epic" },
  { min: 6.5, colour: "#1e4d7a", label: "Great" },
  { min: 5, colour: "#2a5f8f", label: "Good" },
  { min: 3.5, colour: "#7a8fa0", label: "Fair" },
  { min: 0, colour: "#c4ccd4", label: "Poor" },
];

export const PRESET_DESCRIPTIONS: Record<string, string> = {
  balanced: "Equal weight to conditions, crowds, and price",
  conditions: "Prioritise the highest scoring weeks",
  crowds: "Favour quieter periods over peak conditions",
  value: "Cheapest lift pass weeks weighted highest",
};

export type BusynessBand = {
  colour: string;
  label: string;
};

export function busynessBand(crowdPressure: number): BusynessBand {
  if (crowdPressure >= 0.90) return { colour: "#d9534f", label: "Very busy" };
  if (crowdPressure >= 0.75) return { colour: "#e8944a", label: "Busy" };
  if (crowdPressure >= 0.50) return { colour: "#c8a84e", label: "Moderate" };
  return { colour: "#6aaa64", label: "Quiet" };
}

export type PriceBand = {
  colour: string;
  fg: string;
  label: string;
};

export function priceBand(priceEur: number, minPrice: number, maxPrice: number): PriceBand {
  const range = maxPrice - minPrice;
  if (range <= 0) return { colour: "#6aaa64", fg: "#ffffff", label: "Low" };
  const ratio = (priceEur - minPrice) / range;
  if (ratio <= 0.33) return { colour: "#6aaa64", fg: "#ffffff", label: "Low" };
  if (ratio <= 0.66) return { colour: "#c8a84e", fg: "#ffffff", label: "Mid" };
  return { colour: "#d9534f", fg: "#ffffff", label: "High" };
}

export function getMonthLabel(weekStart: string): string {
  const d = new Date(weekStart + "T00:00:00Z");
  return d.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" });
}

export function getMonthBoundaries(weeks: { weekStart: string }[]): Set<number> {
  const boundaries = new Set<number>();
  for (let i = 1; i < weeks.length; i++) {
    const prevMonth = new Date(weeks[i - 1].weekStart + "T00:00:00Z").getUTCMonth();
    const currMonth = new Date(weeks[i].weekStart + "T00:00:00Z").getUTCMonth();
    if (currMonth !== prevMonth) boundaries.add(i);
  }
  return boundaries;
}

export function formatWeekLabel(weekStart: string): string {
  const d = new Date(weekStart + "T00:00:00Z");
  const month = d.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" });
  const weekInMonth = Math.ceil(d.getUTCDate() / 7);
  return `${month} W${weekInMonth}`;
}

export function formatWeekCommencing(weekStart: string): string {
  const d = new Date(weekStart + "T00:00:00Z");
  const day = d.getUTCDate();
  const mon = d.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" });
  return `w/c ${day} ${mon}`;
}

export function generateWhyLine(
  week: BestTimeWeek,
  weekLabel: string,
  conditions: number
): string {
  const crowdLabel = busynessBand(week.crowdPressure).label.toLowerCase();
  if (week.conditionsMissing) {
    return `${weekLabel}: ${crowdLabel} crowds. There is no historical conditions score for this week yet.`;
  }
  const condLabel = conditionsBand(conditions).label.toLowerCase();
  const condStr = String(scoreOutOf100(conditions));

  if (conditions >= 6.5 && week.crowdPressure < 0.50) {
    return `${weekLabel} combines ${condLabel} conditions (${condStr}/100) with low crowds. A sweet spot.`;
  }
  if (conditions >= 6.5 && week.crowdPressure >= 0.80) {
    return `${weekLabel} has ${condLabel} conditions (${condStr}/100), though expect ${crowdLabel} periods from overlapping holidays.`;
  }
  if (conditions >= 6.5) {
    return `${weekLabel} offers ${condLabel} conditions (${condStr}/100) with ${crowdLabel} crowds.`;
  }
  if (week.crowdPressure < 0.50) {
    return `${weekLabel} is ${crowdLabel} with ${condLabel} conditions (${condStr}/100). Good for avoiding crowds.`;
  }
  return `${weekLabel}: ${condLabel} conditions (${condStr}/100), ${crowdLabel} crowds.`;
}

export function overallScoreDisplay(score: number): {
  pct: number;
  colour: string;
  textColour: string;
} {
  const pct = Math.round(score * 100);
  if (pct >= 75) return { pct, colour: "#f5c543", textColour: "#1B2840" };
  if (pct >= 58) return { pct, colour: "#1e4d7a", textColour: "#ffffff" };
  if (pct >= 42) return { pct, colour: "#2a5f8f", textColour: "#ffffff" };
  if (pct >= 28) return { pct, colour: "#7a8fa0", textColour: "#ffffff" };
  return { pct, colour: "#c4ccd4", textColour: "#1B2840" };
}

export function countryFlag(countryCode: string): string {
  return countryCode
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

export const SPORT_TAGS: Record<string, string[]> = {
  ski: ["Large Ski Area", "Small and Charming", "High Altitude", "Beginner-Friendly", "Expert Terrain", "Family", "Party Scene", "Tree Skiing"],
  "off-piste": ["Backcountry Access", "Steep Couloirs", "Glacier Terrain", "Guided Only", "Freeride Hub"],
  touring: ["Backcountry Access", "Glacier Terrain", "Guided Only", "Hut-to-Hut"],
  mtb: ["Lift-Served", "Natural Trails", "Enduro", "Downhill", "Cross-Country", "Beginner-Friendly"],
  surf: ["Beach Break", "Reef Break", "Point Break", "Beginner-Friendly", "Big Wave", "Consistent", "Warm Water", "Remote"],
  wind: ["Flatwater", "Open Ocean", "Consistent Wind", "Beginner-Friendly", "Warm Water"],
  foil: ["Flatwater", "Open Ocean", "Consistent Wind", "Beginner-Friendly", "Warm Water"],
  diving: ["Reef", "Wreck", "Cave", "Warm Water", "Cold Water", "Great Visibility"],
  swimming: ["Warm Water", "Cold Water", "Beginner-Friendly"],
  sup: ["Flatwater", "Open Ocean", "Beginner-Friendly", "Warm Water"],
  sailing: ["Flatwater", "Open Ocean", "Consistent Wind", "Warm Water"],
};
